import classNames from "classnames"
import React, {ReactElement} from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircle } from "@fortawesome/free-solid-svg-icons"

import styles from "../../styles/PlanCard_v2.module.scss"
import { PlanIds, PlanInterval, PlanType, ProductCategory, ProductType } from "../../reducers/models"
import GenericLoader from "../helpers/GenericLoader"

const PLAN_TITLE_BREAKOVER_CHAR_COUNT = 10

export type PlanCardProps = {
  planId: PlanIds
  productType: ProductType
  planType: PlanType
  planInterval: PlanInterval
  productCategory: ProductCategory
  planName: string
  trialDays: number
  cost: string
  discountedCost?: string
  preBundleCost?: string
  bundleCostExtraText?: string
  useSaleLayout?: boolean // should be true if *any* price listed in the overview is on sale
  featured?: boolean
  badgeText?: string
  features: string[] // might need to change to {description:string, otherMetaData:?}[] if the feature list gets more complicated per-item
  nonGridFeatures?: string[]
  isLoading?: boolean
  onPurchaseClicked?:(product:ProductType, planId:PlanIds)=>void
  disableButton?: boolean
}

function renderTitle(planName:string):ReactElement {
  const text = planName
  const isLongTitle = text.length > PLAN_TITLE_BREAKOVER_CHAR_COUNT
  return (
    <div className={classNames(styles.title, {[styles.longTitle]:isLongTitle})}>
      {text}
    </div>
  )
}

function renderBadge(text?:string):ReactElement|undefined {
  const notBadged = text === undefined
  // this is a bit hacky, but it keeps the fake-grid layout lined up better than hardcoded css offsets
  // the css class for notBadged hides the badge, but it still takes up the same amount of space
  if (notBadged) {
    text = "-"
  }
  return (
    <div className={classNames(styles.badge, {[styles.notBadged]: notBadged})}>
      {text}
    </div>
  )
}

function renderPriceSection(cost:string, planType:PlanType, planInterval: PlanInterval, trialDays: number, isLoading:boolean, discountedCost:string|undefined, preBundleCost:string|undefined, bundleCostExtraText:string|undefined):ReactElement {
  // render prices to two decimals
  cost = parseFloat(cost).toFixed(2)
  if(discountedCost !== undefined){
    discountedCost = parseFloat(discountedCost).toFixed(2)
  }
  if(preBundleCost !== undefined){
    preBundleCost = parseFloat(preBundleCost).toFixed(2)
  }

  // here be the weird rules of the cost sub-text sea
  const onSale = discountedCost !== undefined
  const isFree = parseFloat(cost) < 0.01
  const isBundle = preBundleCost !== undefined
  const showBundleCostExtraText = isBundle && bundleCostExtraText !== undefined
  const showBundleValue = isBundle && !showBundleCostExtraText
  const showOriginalPrice = onSale //&& !showBundleValue
  const termText = planInterval === PlanInterval.Monthly ? "month" : planInterval === PlanInterval.Weekly ? "week" : "year"
  //const renderedCost = preBundleCost !== undefined? preBundleCost : cost
  const renderedCost = cost

  const toNumber =(parseFloat(onSale && discountedCost ? discountedCost : cost)/ 52).toFixed(2)
  
  function renderCostText() {
    if(planInterval === PlanInterval.Monthly){
      return <>
        <p className={styles.pikepriceMainPeriodMonth}>${(parseFloat(onSale && discountedCost ? discountedCost : cost)*12/52).toFixed(2)} <span>/week</span></p>
        {isFree ? null : <span className={styles.pikepriceMainPeriod}>billed monthly as ${onSale? discountedCost: cost}</span>}
      </>
    } 
    else if(planInterval === PlanInterval.Weekly){
      return <>
        <p className={styles.pikepriceMainPeriodMonth}>${onSale? discountedCost: cost} <span>/week</span></p>
        {isFree ? null : <span className={styles.pikepriceMainPeriod}>billed weekly</span>}
      </>
    } 
    else if(planInterval === PlanInterval.Yearly){ 
      return <>
        <p className={styles.pikepriceMainPeriodMonth}>${toNumber} <span>/week</span></p>
        {isFree ? null : <span className={styles.pikepriceMainPeriod}>billed yearly as ${onSale? discountedCost: cost}</span>}
      </> 
    }
    else if(planInterval === PlanInterval.Lifetime){
      return <>
        {isFree ? <>
          <span className={styles.priceMainValue}>$0.00</span>
          <span className={styles.priceMainPeriod}> /week</span>
        </> : 
          <p className={styles.pikepriceMainPeriodMonth}>${toNumber} <span> one time</span></p>
        }
      </> 
    }
    else {
      throw new Error(`Unknown subscription billing term ${planInterval}!`)
    }
  }

  return (
    <div className={styles.priceSection}>
      <div className={styles.mainPrice}>
        { renderCostText() }
      </div>
      {
        isLoading? (
          <div className={styles.priceLoadingSpinner}>
            <GenericLoader />
          </div>
        )
        : undefined
      }
      {
        showOriginalPrice ? (
          <>
            <div className={styles.priceOld}>
              <span className={styles.priceStrikeThroughWrapper}>
                <span className={styles.priceOldValue}>${renderedCost}</span>
                <span className={styles.priceOldPeriod}>/{termText}</span>
              </span>
            </div>
            {
              showBundleCostExtraText ? (
                <div className={styles.bundleCostExtraText}>
                  <span>{bundleCostExtraText}</span>
                </div>
              )
                : undefined
            }
          </>
        )
        : undefined
      }
      {
        showBundleValue ? (
          <div className={styles.bundleValue}>
            <span>A ${preBundleCost}/{termText} value</span>
          </div>
        )
        : undefined
      }
      {
        trialDays > 0 ? 
           <div className={styles.trialValue}>
            <span className={styles.trialText}>{trialDays} Day Free Trial</span>
          </div>
        : undefined
      }
    </div>
  )
}

function renderBuyButton(planType:PlanType, trialDays: number, onPurchaseClick:()=>void, disableButton: boolean):ReactElement {
  let text = "Buy Now"
 
  if(planType === PlanType.Free){
    text = "Free"
  }
  else if (disableButton){
    text = "Current Plan"
  }
  else if (trialDays > 0){  
    text = "Start Your Free Trial"
  }
  else {
    text = "Buy Now"
  }
  return <button disabled={disableButton} className={classNames(styles.button, styles[planType])} onClick={onPurchaseClick}>{text}</button>
}

function renderFeature(planType:PlanType, feature:string):ReactElement {
  return (
    <li key={`${planType}_${feature}`}>
      {/* <i><FontAwesomeIcon icon={faCircle} color={useBundleLayout ? "black" : "white"} size={"sm"}/></i> */}
      <i><FontAwesomeIcon icon={faCircle} size={"sm"}/></i>
      <span>{feature}</span>
    </li>
  )
}

function renderNonGridFeatures(planType:PlanType, features:string[]=[]):ReactElement|undefined {
  if (features.length === 0) {
    return undefined
  }
  return (
    <ul className={classNames(styles.featureList, styles.nonGrid)}>
      {features.map(feature => renderFeature(planType, feature))}
    </ul>
  )
}

export default function PlanCard({
  planId,
  productType,
  planType,
  planInterval,
  planName,
  trialDays,
  cost,
  discountedCost,
  preBundleCost,
  bundleCostExtraText,
  featured: highlighted=false,
  useSaleLayout=false,
  features,
  nonGridFeatures,
  badgeText,
  isLoading=true,
  onPurchaseClicked: onPurchaseClick,
  disableButton=false
}:PlanCardProps):ReactElement {
  const onSale = planType !== PlanType.Free &&  discountedCost !== undefined

  function handlePurchaseClick() {
    if (onPurchaseClick !== undefined) {
      onPurchaseClick(productType, planId)
    }
    else {
      console.error("onPurchaseClick not defined for PlanCard")
    }
  }

  return (
    <div className={classNames(styles.planCard, styles[planType], styles[productType], {
        [styles.onSale]:onSale,
        [styles.saleLayout]:useSaleLayout,
        [styles.highlighted]:highlighted && !isLoading,
        [styles.loading]:isLoading
    })}>
      {renderBadge(badgeText)}
      {renderPriceSection(cost, planType, planInterval, trialDays, isLoading, discountedCost, preBundleCost, bundleCostExtraText)}
      {renderTitle(planName)}
      {renderBuyButton(planType, trialDays, handlePurchaseClick, disableButton)}
      <ul className={classNames(styles.featureList)}>
        {features.map(feature => renderFeature(planType, feature))}
      </ul>
      {renderNonGridFeatures(planType, nonGridFeatures)}
    </div>
  )
}
